import { User } from 'firebase/auth';
import { UserService, ProfileService, AnalyticsService } from './databaseService';
import { BillingService } from './billingService';
import { DatabaseRetry, DatabaseValidator } from '../utils/databaseUtils';
import { supabase } from '../supabase/client';
import { findOrCreateStripeCustomer } from './stripeService';

// Enhanced user data interface
export interface EnhancedUserData {
  firebase_uid: string;
  email: string;
  display_name?: string;
  photo_url?: string;
  phone_number?: string;
  first_name?: string;
  last_name?: string;
  company?: string;
  job_title?: string;
  bio?: string;
}

// Authentication service that integrates with all database tables
export class AuthService {
  /**
   * Complete user setup - creates records in users, user_profiles, and user_billing tables
   */
  static async setupNewUser(firebaseUser: User, additionalData: {
    company?: string;
    job_title?: string;
    bio?: string;
    location?: string;
    website?: string;
  } = {}): Promise<{
    success: boolean;
    userRecord?: any;
    profileRecord?: any;
    billingRecord?: any;
    error?: string;
  }> {
    console.log('🚀 Setting up new user:', firebaseUser.uid);

    try {
      // Prepare user data
      const userData: EnhancedUserData = {
        firebase_uid: firebaseUser.uid,
        email: firebaseUser.email || '',
        display_name: firebaseUser.displayName || undefined,
        photo_url: firebaseUser.photoURL || undefined,
        phone_number: firebaseUser.phoneNumber || undefined,
        first_name: this.extractFirstName(firebaseUser.displayName),
        last_name: this.extractLastName(firebaseUser.displayName),
        company: additionalData.company,
        job_title: additionalData.job_title,
        bio: additionalData.bio
      };

      // Validate user data
      const validation = DatabaseValidator.validateUserData(userData);
      if (!validation.isValid) {
        throw new Error(`Invalid user data: ${validation.errors.join(', ')}`);
      }

      // Step 1: Initialize user billing (this is what works reliably)
      console.log('💳 Initializing user billing and account...');
      let billingData: any = null;
      
      // First check if billing already exists (may have been created by BillingContext in parallel)
      const existingBilling = await BillingService.getUserBilling(firebaseUser.uid);
      if (existingBilling) {
        console.log('✅ Billing already exists (created by BillingContext), using existing:', {
          user_id: existingBilling.user_id,
          credits: existingBilling.credits,
          plan: existingBilling.current_plan
        });
        billingData = existingBilling;
      } else {
        // Try to initialize billing
        const billingResult = await DatabaseRetry.withRetry(
          () => supabase.rpc('initialize_user_billing', {
            p_user_id: firebaseUser.uid,
            p_email: userData.email
          }),
          { operationName: 'Initialize User Billing', maxAttempts: 3 }
        );

        if (billingResult.error) {
          // Handle duplicate key error gracefully - billing was likely created in parallel
          const isDuplicateKey = billingResult.error.code === '23505' || 
                                  billingResult.error.message?.includes('duplicate key') ||
                                  billingResult.error.message?.includes('unique constraint');
          
          if (isDuplicateKey) {
            console.log('ℹ️ Billing record already exists (race condition), fetching existing...');
            const fetchedBilling = await BillingService.getUserBilling(firebaseUser.uid);
            if (fetchedBilling) {
              billingData = fetchedBilling;
              console.log('✅ Retrieved existing billing record:', {
                user_id: fetchedBilling.user_id,
                credits: fetchedBilling.credits,
                plan: fetchedBilling.current_plan
              });
            } else {
              console.warn('⚠️ Could not fetch billing after duplicate key error');
            }
          } else {
            throw new Error(`Failed to initialize user billing: ${billingResult.error.message}`);
          }
        } else {
          billingData = billingResult.data as any;
          console.log('✅ User billing initialized successfully:', {
            user_id: billingData.user_id,
            email: billingData.email,
            credits: billingData.credits,
            plan: billingData.current_plan
          });
        }
      }

      // Step 2: Find or create Stripe customer
      console.log('💳 Finding or creating Stripe customer...');
      let stripeCustomerId: string | undefined;
      try {
        const stripeResult = await findOrCreateStripeCustomer({
          email: userData.email,
          name: userData.display_name,
          firebaseUid: firebaseUser.uid,
          phone: userData.phone_number,
          metadata: {
            firebase_uid: firebaseUser.uid,
            company: additionalData.company || '',
            job_title: additionalData.job_title || '',
          }
        });

        if (stripeResult.success && stripeResult.customer) {
          stripeCustomerId = stripeResult.customer.customerId;
          if (stripeResult.existingCustomer) {
            console.log('✅ Found existing Stripe customer:', stripeCustomerId);
          } else {
            console.log('✅ Stripe customer created successfully:', stripeCustomerId);
          }

          // Update user_billing with Stripe customer ID
          // Since billing record was just created by initialize_user_billing RPC, we use update
          const { error: updateError, data: updatedBilling } = await supabase
            .from('user_billing')
            .update({ 
              stripe_customer_id: stripeCustomerId,
              updated_at: new Date().toISOString()
            })
            .eq('user_id', firebaseUser.uid)
            .select()
            .single();
          
          if (updateError) {
            // Check if this is a stripe_customer_id unique constraint violation
            // This happens when another user already has this Stripe customer ID (same email, different Firebase UID)
            const isStripeCustomerIdConstraint = updateError.message?.includes('user_billing_stripe_customer_id_key');
            
            if (isStripeCustomerIdConstraint) {
              console.log('ℹ️ Stripe customer ID already exists for another user (same email), treating as success');
              // This is OK - user will share Stripe customer with another account
            } else {
              // If update fails, try upsert as fallback (in case billing record doesn't exist)
              console.warn('⚠️ Update failed, trying upsert as fallback:', updateError.message);
              const { error: upsertError } = await supabase
                .from('user_billing')
                .upsert({ 
                  user_id: firebaseUser.uid,
                  email: userData.email,
                  stripe_customer_id: stripeCustomerId,
                  updated_at: new Date().toISOString()
                }, {
                  onConflict: 'user_id'
                });
              
              if (upsertError) {
                // Check again for stripe_customer_id constraint
                const isStripeConstraintOnUpsert = upsertError.message?.includes('user_billing_stripe_customer_id_key');
                
                if (isStripeConstraintOnUpsert) {
                  console.log('ℹ️ Stripe customer ID already exists (upsert), treating as success');
                } else {
                  console.error('❌ CRITICAL: Failed to save Stripe customer ID to user_billing:', {
                    error: upsertError.message,
                    code: upsertError.code,
                    details: upsertError.details,
                    hint: upsertError.hint,
                    stripeCustomerId
                  });
                }
                // Don't throw - user creation should still succeed
              } else {
                console.log('✅ Stripe customer ID saved to user_billing via upsert fallback');
              }
            }
          } else {
            console.log('✅ Stripe customer ID saved to user_billing:', {
              firebase_uid: firebaseUser.uid,
              stripe_customer_id: stripeCustomerId,
              updated: updatedBilling?.updated_at
            });
          }
        } else {
          console.error('❌ CRITICAL: Failed to find or create Stripe customer:', {
            error: stripeResult.error,
            email: userData.email,
            firebaseUid: firebaseUser.uid
          });
          // Log as error but don't throw - user creation should still succeed
        }
      } catch (stripeError: any) {
        console.error('❌ CRITICAL: Stripe customer find/create exception:', {
          error: stripeError?.message || stripeError,
          stack: stripeError?.stack,
          email: userData.email,
          firebaseUid: firebaseUser.uid
        });
        // Log as error but don't throw - user creation should still succeed
      }

      // Step 3: Store user profile data in localStorage for now (since RLS blocks user_profiles)
      console.log('👤 Storing user profile data locally...');
      const profileData = {
        firebase_uid: firebaseUser.uid,
        email: userData.email,
        display_name: userData.display_name,
        photo_url: userData.photo_url,
        company: additionalData.company,
        job_title: additionalData.job_title,
        bio: additionalData.bio,
        location: additionalData.location,
        website: additionalData.website,
        created_at: new Date().toISOString()
      };

      // Store in localStorage as fallback
      if (typeof window !== 'undefined') {
        localStorage.setItem(`user_profile_${firebaseUser.uid}`, JSON.stringify(profileData));
        console.log('✅ User profile data stored locally');
      }

      // Billing already initialized above via RPC function

      // Step 4: Track user registration event
      console.log('📊 Tracking registration event...');
      try {
        await AnalyticsService.trackEvent({
          firebase_uid: firebaseUser.uid,
          event_type: 'user_registration',
          event_category: 'authentication',
          event_action: 'new_user_signup',
          event_label: this.getAuthProvider(firebaseUser),
          event_metadata: {
            provider: this.getAuthProvider(firebaseUser),
            has_display_name: !!firebaseUser.displayName,
            has_photo: !!firebaseUser.photoURL,
            email_verified: firebaseUser.emailVerified,
            creation_time: firebaseUser.metadata.creationTime,
            registration_source: 'auth_service'
          }
        });
        console.log('✅ Registration event tracked successfully');
      } catch (analyticsError) {
        console.warn('⚠️ Failed to track registration event (non-critical):', analyticsError);
      }

      // Step 5: Update user activity
      try {
        await UserService.updateUserActivity(firebaseUser.uid);
        console.log('✅ User activity updated successfully');
      } catch (activityError) {
        console.warn('⚠️ Failed to update user activity (non-critical):', activityError);
      }

      console.log('🎉 User setup completed successfully!');

      return {
        success: true,
        userRecord: null, // Users table blocked by RLS
        profileRecord: profileData, // Stored locally
        billingRecord: billingData
      };

    } catch (error: any) {
      console.error('❌ User setup failed:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Check if user exists and create if needed
   */
  static async ensureUserExists(firebaseUser: User, additionalData: {
    company?: string;
    job_title?: string;
    bio?: string;
    location?: string;
    website?: string;
  } = {}): Promise<{
    exists: boolean;
    created: boolean;
    userRecord?: any;
    profileRecord?: any;
    error?: string;
  }> {
    try {
      console.log('🔍 Checking if user exists:', firebaseUser.uid);
      
      // Check if user exists in users table
      const existingUser = await UserService.getUserByFirebaseUid(firebaseUser.uid);
      
      // Log the result for debugging
      console.log('📊 User lookup result:', {
        hasData: !!existingUser.data,
        hasError: !!existingUser.error,
        errorCode: existingUser.error?.code,
        errorMessage: existingUser.error?.message
      });
      
      if (existingUser.data) {
        console.log('✅ User already exists, updating activity...');
        
        // Update last activity
        await UserService.updateUserActivity(firebaseUser.uid);
        
        // Check if user has a Stripe customer ID and find or create one if missing
        try {
          console.log('🔍 Checking if user has Stripe customer...');
          let billingData = await BillingService.getUserBilling(firebaseUser.uid);
          
          // If billing data doesn't exist, initialize it first
          if (!billingData) {
            console.log('💳 Billing data not found, initializing...');
            try {
              billingData = await BillingService.initializeUserBilling(
                firebaseUser.uid,
                firebaseUser.email || undefined
              );
              console.log('✅ Billing data initialized');
            } catch (initError) {
              console.warn('⚠️ Failed to initialize billing data (non-critical):', initError);
            }
          }
          
          // Skip if user already has a Stripe customer ID - no need to create duplicate
          if (billingData?.stripe_customer_id) {
            console.log('✅ User already has Stripe customer:', billingData.stripe_customer_id);
          } else {
            // User doesn't have Stripe customer ID - find or create one
            // Note: findOrCreateStripeCustomer will search Stripe first by email to avoid duplicates
            console.log('💳 User missing Stripe customer, finding or creating now...');
            
            const stripeResult = await findOrCreateStripeCustomer({
              email: firebaseUser.email || '',
              name: firebaseUser.displayName || undefined,
              firebaseUid: firebaseUser.uid,
              phone: firebaseUser.phoneNumber || undefined,
              metadata: {
                firebase_uid: firebaseUser.uid,
                created_via: 'backfill_on_login',
                provider: this.getAuthProvider(firebaseUser),
              }
            });

            if (stripeResult.success && stripeResult.customer) {
              if (stripeResult.existingCustomer) {
                console.log('✅ Found existing Stripe customer:', stripeResult.customer.customerId);
              } else {
                console.log('✅ Stripe customer created successfully:', stripeResult.customer.customerId);
              }
              
              // Update user_billing with Stripe customer ID
              // Try update first, then upsert as fallback
              const { error: updateError, data: updatedBilling } = await supabase
                .from('user_billing')
                .update({ 
                  stripe_customer_id: stripeResult.customer.customerId,
                  updated_at: new Date().toISOString()
                })
                .eq('user_id', firebaseUser.uid)
                .select()
                .single();
              
              if (updateError) {
                // Check if this is a stripe_customer_id unique constraint violation
                const isStripeCustomerIdConstraint = updateError.message?.includes('user_billing_stripe_customer_id_key');
                
                if (isStripeCustomerIdConstraint) {
                  console.log('ℹ️ Stripe customer ID already exists for another user (same email), treating as success');
                } else {
                  // If update fails, try upsert as fallback (in case billing record doesn't exist)
                  console.warn('⚠️ Update failed, trying upsert as fallback:', updateError.message);
                  const { error: upsertError } = await supabase
                    .from('user_billing')
                    .upsert({ 
                      user_id: firebaseUser.uid,
                      email: firebaseUser.email || undefined,
                      stripe_customer_id: stripeResult.customer.customerId,
                      updated_at: new Date().toISOString()
                    }, {
                      onConflict: 'user_id'
                    });
                  
                  if (upsertError) {
                    // Check again for stripe_customer_id constraint
                    const isStripeConstraintOnUpsert = upsertError.message?.includes('user_billing_stripe_customer_id_key');
                    
                    if (isStripeConstraintOnUpsert) {
                      console.log('ℹ️ Stripe customer ID already exists (upsert), treating as success');
                    } else {
                      console.error('❌ CRITICAL: Failed to save Stripe customer ID to user_billing:', {
                        error: upsertError.message,
                        code: upsertError.code,
                        details: upsertError.details,
                        hint: upsertError.hint,
                        stripeCustomerId: stripeResult.customer.customerId
                      });
                    }
                  } else {
                    console.log('✅ Stripe customer ID saved to user_billing via upsert fallback');
                  }
                }
              } else {
                console.log('✅ Stripe customer ID saved to user_billing:', {
                  firebase_uid: firebaseUser.uid,
                  stripe_customer_id: stripeResult.customer.customerId,
                  updated: updatedBilling?.updated_at
                });
              }
            } else {
              console.error('❌ CRITICAL: Failed to find or create Stripe customer:', {
                error: stripeResult.error,
                email: firebaseUser.email,
                firebaseUid: firebaseUser.uid
              });
            }
          }
        } catch (stripeError) {
          console.warn('⚠️ Error checking/finding/creating Stripe customer (non-critical):', stripeError);
        }
        
        // Track login event
        try {
          await AnalyticsService.trackEvent({
            firebase_uid: firebaseUser.uid,
            event_type: 'user_login',
            event_category: 'authentication',
            event_action: 'existing_user_login',
            event_label: this.getAuthProvider(firebaseUser),
            event_metadata: {
              provider: this.getAuthProvider(firebaseUser),
              last_sign_in: firebaseUser.metadata.lastSignInTime,
              login_source: 'auth_service'
            }
          });
        } catch (analyticsError) {
          console.warn('⚠️ Failed to track login event (non-critical):', analyticsError);
        }

        return {
          exists: true,
          created: false,
          userRecord: existingUser.data
        };
      }

      // Check if there was an error other than "not found"
      if (existingUser.error && existingUser.error.code !== 'PGRST116') {
        console.warn('⚠️ Error checking user existence:', existingUser.error);
        // If we can't determine if user exists, try to create and handle duplicate error
        console.log('⚠️ Will attempt to create user, may encounter duplicate key error if user exists');
      }

      // User doesn't exist (or we couldn't check), try to create new user
      console.log('🆕 User not found, creating new user...');
      
      try {
        const setupResult = await this.setupNewUser(firebaseUser, additionalData);
        
        return {
          exists: false,
          created: setupResult.success,
          userRecord: setupResult.userRecord,
          profileRecord: setupResult.profileRecord,
          error: setupResult.error
        };
      } catch (setupError: any) {
        // Check if this is a duplicate key error (user actually exists)
        if (setupError.message?.includes('duplicate key') || 
            setupError.message?.includes('unique constraint')) {
          console.log('⚠️ User creation failed due to duplicate - user exists, fetching existing record...');
          
          // Wait a moment and try to fetch the existing user again
          await new Promise(resolve => setTimeout(resolve, 500));
          const retryUser = await UserService.getUserByFirebaseUid(firebaseUser.uid);
          
          if (retryUser.data) {
            console.log('✅ Found existing user after duplicate error');
            return {
              exists: true,
              created: false,
              userRecord: retryUser.data
            };
          }
        }
        
        // Re-throw if it's a different error
        throw setupError;
      }

    } catch (error: any) {
      console.error('❌ Error checking/creating user:', error);
      return {
        exists: false,
        created: false,
        error: error.message
      };
    }
  }

  /**
   * Update user profile information
   */
  static async updateUserProfile(firebaseUser: User, updates: {
    display_name?: string;
    company?: string;
    job_title?: string;
    bio?: string;
    location?: string;
    website?: string;
    phone_number?: string;
  }): Promise<{ success: boolean; error?: string }> {
    try {
      // Update users table
      const userUpdates: any = {};
      if (updates.display_name) userUpdates.display_name = updates.display_name;
      if (updates.phone_number) userUpdates.phone_number = updates.phone_number;
      if (updates.company) userUpdates.company = updates.company;
      if (updates.job_title) userUpdates.job_title = updates.job_title;
      if (updates.bio) userUpdates.bio = updates.bio;

      if (Object.keys(userUpdates).length > 0) {
        const userResult = await UserService.updateUser(firebaseUser.uid, userUpdates);
        if (userResult.error) {
          throw new Error(`Failed to update user: ${userResult.error.message}`);
        }
      }

      // Update user_profiles table
      const profileUpdates: any = {};
      if (updates.display_name) profileUpdates.display_name = updates.display_name;
      if (updates.company) profileUpdates.company = updates.company;
      if (updates.job_title) profileUpdates.role = updates.job_title;
      if (updates.bio) profileUpdates.bio = updates.bio;
      if (updates.location) profileUpdates.location = updates.location;
      if (updates.website) profileUpdates.website = updates.website;

      if (Object.keys(profileUpdates).length > 0) {
        const profileResult = await ProfileService.updateProfile(firebaseUser.uid, profileUpdates);
        if (profileResult.error) {
          console.warn('⚠️ Failed to update profile (non-critical):', profileResult.error.message);
        }
      }

      // Track profile update event
      try {
        await AnalyticsService.trackEvent({
          firebase_uid: firebaseUser.uid,
          event_type: 'profile_updated',
          event_category: 'user_management',
          event_action: 'update_profile',
          event_metadata: {
            updated_fields: Object.keys({ ...userUpdates, ...profileUpdates }),
            update_source: 'auth_service'
          }
        });
      } catch (analyticsError) {
        console.warn('⚠️ Failed to track profile update event (non-critical):', analyticsError);
      }

      return { success: true };

    } catch (error: any) {
      console.error('❌ Error updating user profile:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get complete user data from all tables
   */
  static async getCompleteUserData(firebaseUid: string): Promise<{
    user?: any;
    profile?: any;
    billing?: any;
    analytics?: any[];
    error?: string;
  }> {
    try {
      // Get user data from users table
      const userResult = await UserService.getUserByFirebaseUid(firebaseUid);
      
      // Get profile data from user_profiles table
      const profileResult = await ProfileService.getProfile(firebaseUid);
      
      // Get billing data
      let billingData = null;
      try {
        billingData = await BillingService.getUserBilling(firebaseUid);
      } catch (billingError) {
        console.warn('⚠️ Failed to get billing data (non-critical):', billingError);
      }

      // Get recent analytics
      const analyticsResult = await AnalyticsService.getUserAnalytics(firebaseUid, { limit: 10 });

      return {
        user: userResult.data,
        profile: profileResult.data,
        billing: billingData,
        analytics: analyticsResult.data || [],
        error: userResult.error?.message || profileResult.error?.message
      };

    } catch (error: any) {
      console.error('❌ Error getting complete user data:', error);
      return { error: error.message };
    }
  }

  /**
   * Soft delete user (GDPR compliance)
   */
  static async deleteUser(firebaseUid: string): Promise<{ success: boolean; error?: string }> {
    try {
      // Soft delete user record
      const userResult = await UserService.softDeleteUser(firebaseUid);
      if (userResult.error) {
        throw new Error(`Failed to delete user: ${userResult.error.message}`);
      }

      // Delete user profile
      const profileResult = await ProfileService.deleteProfile(firebaseUid);
      if (profileResult.error) {
        console.warn('⚠️ Failed to delete profile (non-critical):', profileResult.error.message);
      }

      // Delete old analytics data (keep recent for audit)
      try {
        await AnalyticsService.deleteUserAnalytics(firebaseUid, 90); // Keep last 90 days
      } catch (analyticsError) {
        console.warn('⚠️ Failed to cleanup analytics (non-critical):', analyticsError);
      }

      // Track deletion event
      try {
        await AnalyticsService.trackEvent({
          firebase_uid: firebaseUid,
          event_type: 'user_deleted',
          event_category: 'user_management',
          event_action: 'account_deletion',
          event_metadata: {
            deletion_time: new Date().toISOString(),
            deletion_source: 'auth_service'
          }
        });
      } catch (analyticsError) {
        console.warn('⚠️ Failed to track deletion event (non-critical):', analyticsError);
      }

      return { success: true };

    } catch (error: any) {
      console.error('❌ Error deleting user:', error);
      return { success: false, error: error.message };
    }
  }

  // Helper methods
  private static extractFirstName(displayName?: string | null): string | undefined {
    if (!displayName) return undefined;
    return displayName.split(' ')[0] || undefined;
  }

  private static extractLastName(displayName?: string | null): string | undefined {
    if (!displayName) return undefined;
    const parts = displayName.split(' ');
    return parts.length > 1 ? parts.slice(1).join(' ') : undefined;
  }

  private static getAuthProvider(user: User): string {
    if (user.providerData.length > 0) {
      const providerId = user.providerData[0].providerId;
      switch (providerId) {
        case 'google.com': return 'google';
        case 'github.com': return 'github';
        case 'password': return 'email';
        default: return providerId;
      }
    }
    return 'unknown';
  }
}

export default AuthService;
